import { yesNoPreferOptions } from './03-electoral.js';
import type { FieldGroup } from './types.js';

export const politicalSpectrumOptions = [
  {
    value: 'far_left',
    label: 'Far Left',
    description: 'Radical progressive — systemic change, social justice, anti-capitalist',
  },
  {
    value: 'left',
    label: 'Left',
    description: 'Progressive — strong social safety net, equality, regulation',
  },
  {
    value: 'centre_left',
    label: 'Centre-Left',
    description: 'Liberal — social freedoms, moderate reform, inclusive policies',
  },
  {
    value: 'centrist',
    label: 'Centrist',
    description: 'Pragmatic middle — picks issues over ideology, open to both sides',
  },
  {
    value: 'centre_right',
    label: 'Centre-Right',
    description:
      'Moderate conservative — fiscal discipline, traditional institutions, gradual change',
  },
  {
    value: 'right',
    label: 'Right',
    description: 'Conservative — limited government, free markets, cultural tradition',
  },
  {
    value: 'far_right',
    label: 'Far Right',
    description: 'Nationalist — strong borders, traditional values, national identity first',
  },
  {
    value: 'libertarian',
    label: 'Libertarian',
    description: 'Maximum personal freedom — minimal government in both social and economic life',
  },
  {
    value: 'apolitical',
    label: 'Apolitical',
    description: 'Not interested in politics — focus on personal life',
  },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

/**
 * Indian political parties — exhaustive list of nationally- or state-recognized
 * parties active as of 2026. Ordering rationale:
 *   1. `none` + `independent` are pinned to the top — they're the most-
 *      common selections for users who don't identify with any party,
 *      and surfacing them first reduces scroll for the majority case.
 *   2. National parties next (BJP, INC, AAP, BSP, SP, TMC), then state /
 *      regional parties.
 *   3. `other` and `prefer_not_to_say` stay at the bottom (standard
 *      "escape hatch" position).
 *
 * Includes post-2023 splits (NCP-SP, Shiv Sena UBT).
 */
export const partyOptions = [
  { value: 'none', label: 'No party affiliation' },
  { value: 'independent', label: 'Independent candidate' },
  { value: 'bjp', label: 'BJP' },
  { value: 'inc', label: 'Indian National Congress (INC)' },
  { value: 'aap', label: 'Aam Aadmi Party (AAP)' },
  { value: 'bsp', label: 'Bahujan Samaj Party (BSP)' },
  { value: 'sp', label: 'Samajwadi Party (SP)' },
  { value: 'tmc', label: 'Trinamool Congress (TMC)' },
  { value: 'dmk', label: 'DMK' },
  { value: 'aiadmk', label: 'AIADMK' },
  { value: 'ncp', label: 'NCP (Ajit Pawar)' },
  { value: 'ncp_sp', label: 'NCP (Sharad Pawar)' },
  { value: 'shiv_sena', label: 'Shiv Sena (Shinde)' },
  { value: 'shiv_sena_ubt', label: 'Shiv Sena (UBT)' },
  { value: 'aimim', label: 'AIMIM' },
  { value: 'jdu', label: 'JD(U)' },
  { value: 'jds', label: 'JD(S)' },
  { value: 'rjd', label: 'RJD' },
  { value: 'cpim', label: 'CPI(M)' },
  { value: 'cpi', label: 'CPI' },
  { value: 'ysrcp', label: 'YSRCP' },
  { value: 'tdp', label: 'TDP' },
  { value: 'brs', label: 'BRS (Bharat Rashtra Samithi)' },
  { value: 'bjd', label: 'BJD' },
  { value: 'jmm', label: 'JMM' },
  { value: 'sad', label: 'Shiromani Akali Dal (SAD)' },
  { value: 'rld', label: 'Rashtriya Lok Dal (RLD)' },
  { value: 'aiudf', label: 'AIUDF' },
  { value: 'jkn', label: 'J&K National Conference' },
  { value: 'inld', label: 'Indian National Lok Dal (INLD)' },
  { value: 'iuml', label: 'Indian Union Muslim League (IUML)' },
  { value: 'mns', label: 'Maharashtra Navnirman Sena (MNS)' },
  { value: 'agp', label: 'Asom Gana Parishad (AGP)' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

/**
 * Civic-engagement categories — expanded to cover organizational forms
 * common in Indian political/social life, including ideological networks
 * (RSS / Sangh), farmers' bodies, women's collectives, student unions,
 * and party youth wings.
 */
export const civicEngagementOptions = [
  { value: 'ngo', label: 'NGO / Non-profit' },
  { value: 'trade_union', label: 'Trade Union' },
  { value: 'farmers_organization', label: "Farmers' Organization / Kisan Sangathan" },
  { value: 'cooperative', label: 'Cooperative Society' },
  { value: 'resident_association', label: 'Resident Welfare Association' },
  { value: 'self_help_group', label: 'Self Help Group (SHG)' },
  { value: 'womens_collective', label: "Women's Collective / Mahila Mandal" },
  { value: 'youth_club', label: 'Youth Club' },
  { value: 'student_union', label: 'Student Union' },
  { value: 'political_party_youth_wing', label: 'Political Party Youth Wing (BJYM / IYC / etc.)' },
  { value: 'rss_swayamsevak', label: 'RSS / Swayamsevak (Sangh network)' },
  { value: 'religious_organization', label: 'Religious Organization' },
  { value: 'caste_association', label: 'Caste Association' },
  { value: 'professional_body', label: 'Professional Body' },
  { value: 'environmental_group', label: 'Environmental Group' },
  { value: 'cultural_association', label: 'Cultural Association' },
  { value: 'alumni_association', label: 'Alumni Association' },
  { value: 'none', label: 'None' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Nature of the respondent's association with their political party.
 * Differentiates card-carrying members from sympathizers and office-bearers.
 */
export const partyAssociationRoleOptions = [
  {
    value: 'member',
    label: 'Member',
    description: 'Primary / card-carrying member of the party',
  },
  {
    value: 'active_worker',
    label: 'Active Worker',
    description: 'Campaigning, organizing, and on-ground work',
  },
  {
    value: 'office_bearer',
    label: 'Office Bearer',
    description: 'Hold an official post in the party',
  },
  {
    value: 'candidate',
    label: 'Candidate',
    description: 'Contested or planning to contest on this party ticket',
  },
  {
    value: 'spokesperson',
    label: 'Spokesperson',
    description: 'Represent the party publicly or in media',
  },
  {
    value: 'volunteer',
    label: 'Volunteer',
    description: 'Occasional volunteer work without formal membership',
  },
  {
    value: 'sympathizer',
    label: 'Sympathizer',
    description: 'Support the party but no formal role',
  },
  { value: 'none', label: 'None', description: 'No formal association' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

/**
 * Family / relatives in politics — captures intergenerational political
 * involvement that shapes political consciousness in India.
 */
export const relativesInPoliticsOptions = [
  { value: 'none', label: 'No one', description: 'No relatives are in politics' },
  {
    value: 'immediate_family',
    label: 'Immediate family',
    description: 'Parents, siblings, spouse, or children',
  },
  {
    value: 'extended_family',
    label: 'Extended family',
    description: 'Uncles, aunts, cousins, or in-laws',
  },
  { value: 'both', label: 'Both immediate and extended' },
  { value: 'dont_know', label: "Don't know" },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

/**
 * Levels of elected / appointed office — used for both office held and
 * election contested, so the two use the same enum.
 */
export const officeElectionLevelOptions = [
  { value: 'panchayat', label: 'Panchayat / Gram' },
  { value: 'ward', label: 'Ward / Nagar Palika' },
  { value: 'municipality', label: 'Municipality' },
  { value: 'block', label: 'Block / Tehsil' },
  { value: 'district', label: 'District / Zila Parishad' },
  { value: 'assembly', label: 'State Assembly (MLA)' },
  { value: 'parliament', label: 'Parliament (MP)' },
  { value: 'other', label: 'Other' },
] as const;

// `yesNoPreferOptions` (used by hasContestedElection +
// hasHeldPublicOffice) lives in 03-electoral.ts where it was first
// introduced for `didVoteLastTime`. Imported above; not re-defined.

export const politicalAssociation: FieldGroup = {
  key: 'political_association',
  label: 'Political Association & History',
  step: 2,
  description: 'Your political spectrum, party associations, and civic engagement.',
  fields: [
    {
      order: 1,
      key: 'politicalSpectrum',
      label: 'Political Spectrum',
      type: 'single_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...politicalSpectrumOptions],
    },
    {
      order: 2,
      key: 'selfPartyAssociation',
      label: 'Your Party Association',
      type: 'single_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...partyOptions],
    },
    {
      // Only ask about role if the respondent has a party association.
      order: 3,
      key: 'partyAssociationRole',
      label: 'Your Role in the Party',
      type: 'single_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...partyAssociationRoleOptions],
      conditionalOn: {
        field: 'selfPartyAssociation',
        notIn: ['none', 'prefer_not_to_say'],
      },
    },
    {
      order: 4,
      key: 'familyPartyAssociation',
      label: 'Family Party Association',
      type: 'single_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...partyOptions],
    },
    {
      order: 5,
      key: 'relativesInPolitics',
      label: 'Relatives in Politics',
      type: 'single_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...relativesInPoliticsOptions],
    },
    // Replaces the legacy single composite `publicOfficeAndElections`
    // sub-step (a 9-field nested object) with six simpler sub-steps.
    // Each yes/no question gets its own screen; the level + year
    // follow-ups are gated on `yes` so users who answer `no` /
    // `prefer_not_to_say` skip straight past them. Position-name /
    // notes / result fields from the legacy shape are dropped — they
    // were free-text fields with low completion in the dense composite
    // UI, and the simpler shape matches the rest of UDP onboarding.
    {
      order: 6,
      key: 'hasContestedElection',
      label: 'Have you ever contested an election?',
      type: 'single_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...yesNoPreferOptions],
    },
    {
      order: 7,
      key: 'electionLevel',
      label: 'At which level did you contest?',
      type: 'single_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...officeElectionLevelOptions],
      conditionalOn: {
        field: 'hasContestedElection',
        values: ['yes'],
      },
    },
    {
      order: 8,
      key: 'electionYear',
      label: 'In which year did you contest?',
      type: 'number',
      step: 2,
      isOnboarding: true,
      optional: true,
      conditionalOn: {
        field: 'hasContestedElection',
        values: ['yes'],
      },
    },
    {
      order: 9,
      key: 'hasHeldPublicOffice',
      label: 'Have you ever held a public office?',
      type: 'single_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...yesNoPreferOptions],
    },
    {
      order: 10,
      key: 'officeLevel',
      label: 'At which level did you hold office?',
      type: 'single_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...officeElectionLevelOptions],
      conditionalOn: {
        field: 'hasHeldPublicOffice',
        values: ['yes'],
      },
    },
    {
      order: 11,
      key: 'officeYear',
      label: 'In which year did you hold office?',
      type: 'number',
      step: 2,
      isOnboarding: true,
      optional: true,
      conditionalOn: {
        field: 'hasHeldPublicOffice',
        values: ['yes'],
      },
    },
    {
      order: 12,
      key: 'networkAndCivicEngagement',
      label: 'Civic Engagement',
      type: 'multi_select',
      step: 2,
      isOnboarding: true,
      optional: true,
      options: [...civicEngagementOptions],
    },
  ],
};
